import React, {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
  Children,
} from 'react'
import styled, { css } from 'styled-components'

import {
  ChevronLeftExactIcon,
  ChevronRightExactIcon,
} from '@root/shared/ui-kit'

import { MarqueeWheelRootContext } from './marquee-wheel'

import type { ReactNode, RefAttributes } from 'react'

type MarqueeInteractiveProps = {
  autoFill?: boolean
  speed?: number
  delay?: number
  loop?: number
  onFinish?: () => void
  onCycleComplete?: () => void
  onMount?: () => void
  scrollOffset?: number | undefined
  onScroll?: (offset: number) => void
  paused?: boolean
  children?: ReactNode
} & RefAttributes<HTMLDivElement>

export const MarqueeInteractive = React.forwardRef(
  (
    {
      autoFill = true,
      speed = 25,
      onMount,
      paused = false,
      children,
    }: MarqueeInteractiveProps,
    forwardedRef: React.ComponentPropsWithRef<'div'>['ref'],
  ) => {
    const rootRef = useRef<HTMLDivElement | null>(null)
    const contentRef = useRef<HTMLDivElement | null>(null)
    const laneRef = useRef<HTMLDivElement | null>(null)

    const [multiplier, setMultiplier] = useState(1)
    const containerWidthRef = useRef(0)
    const singleWidthRef = useRef(0)

    const offsetRef = useRef(0)
    const userVelRef = useRef(0)
    const baseVelRef = useRef(0)
    const rafRef = useRef<number | undefined>(undefined)
    const lastTimeRef = useRef<number | undefined>(undefined)
    const pausedRef = useRef(false)
    const externallyPausedRef = useRef(paused)

    const isDraggingRef = useRef(false)
    const dragStartXRef = useRef(0)
    const dragStartOffsetRef = useRef(0)
    const lastDragXRef = useRef(0)
    const lastDragTimeRef = useRef(0)

    const lastAppliedOffsetRef = useRef<number>(Number.NaN)
    const prevSWRef = useRef(0)

    // Wraps `offsetRef` into `[0, sw)` and writes the lane transform. Extracted
    // so wheel/drag handlers can commit a frame synchronously (without waiting
    // for the next RAF), keeping input feedback snappy when the main thread is
    // busy with parent re-renders (e.g., on Explore).
    const commitOffset = useCallback(() => {
      const laneEl = laneRef.current
      if (!laneEl) {
        return
      }
      const sw = singleWidthRef.current
      if (sw > 0) {
        offsetRef.current = ((offsetRef.current % sw) + sw) % sw
      }
      const rounded = Math.round(offsetRef.current * 100) / 100
      if (rounded !== lastAppliedOffsetRef.current) {
        laneEl.style.transform = `translate3d(${-rounded}px, 0, 0)`
        lastAppliedOffsetRef.current = rounded
      }
    }, [])

    const tick = useCallback(
      function tick(now: number) {
        const laneEl = laneRef.current
        if (!laneEl) {
          rafRef.current = undefined
          lastTimeRef.current = undefined
          return
        }

        if (lastTimeRef.current == undefined) {
          lastTimeRef.current = now
        }
        const dt = Math.min(0.05, (now - lastTimeRef.current) / 1000)
        lastTimeRef.current = now

        const sw = singleWidthRef.current

        if (prevSWRef.current > 0 && sw > 0 && sw !== prevSWRef.current) {
          offsetRef.current = (offsetRef.current / prevSWRef.current) * sw
        }
        prevSWRef.current = sw

        if (!isDraggingRef.current) {
          const base =
            pausedRef.current || externallyPausedRef.current
              ? 0
              : baseVelRef.current || 0
          const user = userVelRef.current || 0

          offsetRef.current = offsetRef.current + (base + user) * dt

          const halfLife = 0.05
          const decay = Math.pow(0.5, dt / halfLife)
          userVelRef.current = user * decay
        }

        commitOffset()

        const base =
          pausedRef.current || externallyPausedRef.current
            ? 0
            : baseVelRef.current || 0
        const user = userVelRef.current || 0
        const hasMotion =
          isDraggingRef.current || base !== 0 || Math.abs(user) > 0.5

        if (hasMotion) {
          rafRef.current = requestAnimationFrame(tick)
        } else {
          rafRef.current = undefined
          lastTimeRef.current = undefined
        }
      },
      [commitOffset],
    )

    const ensureRaf = useCallback(() => {
      if (rafRef.current !== undefined) {
        return
      }
      lastTimeRef.current = undefined
      rafRef.current = requestAnimationFrame(tick)
    }, [tick])

    const handlePreviousClick = () => {
      userVelRef.current = -speed * 40 * 3
      ensureRaf()

      setTimeout(() => {
        userVelRef.current = 0
      }, 500)
    }
    const handleNextClick = () => {
      userVelRef.current = speed * 40 * 3
      ensureRaf()

      setTimeout(() => {
        userVelRef.current = 0
      }, 500)
    }

    const recalcBaseVel = useCallback(() => {
      const sw = singleWidthRef.current
      const cw = containerWidthRef.current
      if (sw <= 0) {
        baseVelRef.current = 0
        return
      }
      const marqueeWidth = sw * Math.max(1, multiplier)
      const effectiveWidth = !autoFill && marqueeWidth < cw ? cw : marqueeWidth
      const dur = effectiveWidth / Math.max(1, speed)
      baseVelRef.current = sw / Math.max(0.01, dur)
    }, [autoFill, speed, multiplier])

    const calculateWidths = useCallback(() => {
      const contentEl = contentRef.current
      const rootEl = rootRef.current
      if (!contentEl || !rootEl) {
        return
      }

      const containerRect = rootEl.getBoundingClientRect()
      const containerW = containerRect.width
      const singleRect = contentEl.getBoundingClientRect()
      const sWidth = Math.max(1, Math.round(singleRect.width))

      containerWidthRef.current = containerW

      const oldSW = singleWidthRef.current
      if (oldSW > 0 && sWidth > 0 && oldSW !== sWidth) {
        offsetRef.current = (offsetRef.current / oldSW) * sWidth
      }
      singleWidthRef.current = sWidth

      if (autoFill && containerW && sWidth) {
        const mult = sWidth < containerW ? Math.ceil(containerW / sWidth) : 1
        setMultiplier((prev) => (prev === mult ? prev : mult))
      } else {
        setMultiplier((prev) => (prev === 1 ? prev : 1))
      }

      recalcBaseVel()
    }, [autoFill, recalcBaseVel])

    const calculateWidthsRef = useRef(calculateWidths)
    calculateWidthsRef.current = calculateWidths

    useEffect(() => {
      const run = () => calculateWidthsRef.current()
      run()
      const ro = new ResizeObserver(run)
      if (contentRef.current) {
        ro.observe(contentRef.current)
      }
      if (rootRef.current) {
        ro.observe(rootRef.current)
      }
      window.addEventListener('resize', run)
      return () => {
        ro.disconnect()
        window.removeEventListener('resize', run)
      }
    }, [])

    useEffect(() => {
      if (typeof onMount === 'function') {
        onMount()
      }
    }, [onMount])

    useEffect(() => {
      recalcBaseVel()
      if (baseVelRef.current !== 0) {
        ensureRaf()
      }
    }, [recalcBaseVel, ensureRaf])

    useEffect(() => {
      externallyPausedRef.current = paused
      if (!paused && baseVelRef.current !== 0) {
        ensureRaf()
      }
    }, [paused, ensureRaf])

    // Hover pause handlers (only pause base velocity)
    useEffect(() => {
      const rootEl = rootRef.current
      if (!rootEl) {
        return
      }

      const handleMouseEnter = () => {
        pausedRef.current = true
      }
      const handleMouseLeave = () => {
        pausedRef.current = false
        ensureRaf()
      }

      rootEl.addEventListener('mouseenter', handleMouseEnter)
      rootEl.addEventListener('mouseleave', handleMouseLeave)

      return () => {
        rootEl.removeEventListener('mouseenter', handleMouseEnter)
        rootEl.removeEventListener('mouseleave', handleMouseLeave)
      }
    }, [ensureRaf])

    // 🖱️ Drag handlers
    useEffect(() => {
      const rootEl = rootRef.current
      if (!rootEl) {
        return
      }

      const handlePointerMove = (e: PointerEvent) => {
        if (!isDraggingRef.current) {
          return
        }

        const now = performance.now()
        const dx = e.clientX - dragStartXRef.current
        const newOffset = dragStartOffsetRef.current - dx
        const sw = singleWidthRef.current

        offsetRef.current = sw > 0 ? ((newOffset % sw) + sw) % sw : newOffset

        // Calculate velocity for momentum
        const dt = Math.max(1, now - lastDragTimeRef.current)
        const instantVel = ((lastDragXRef.current - e.clientX) / dt) * 1000

        lastDragXRef.current = e.clientX
        lastDragTimeRef.current = now

        // Store velocity for release
        userVelRef.current = instantVel

        e.preventDefault()
      }

      const handlePointerUp = (_e: PointerEvent) => {
        if (!isDraggingRef.current) {
          return
        }

        isDraggingRef.current = false

        // Remove document-level listeners
        document.removeEventListener('pointermove', handlePointerMove)
        document.removeEventListener('pointerup', handlePointerUp)
        document.removeEventListener('pointercancel', handlePointerCancel)

        // userVelRef.current already contains the momentum velocity
        // It will decay naturally in the RAF loop
      }

      const handlePointerCancel = (_e: PointerEvent) => {
        if (!isDraggingRef.current) {
          return
        }

        isDraggingRef.current = false
        userVelRef.current = 0

        // Remove document-level listeners
        document.removeEventListener('pointermove', handlePointerMove)
        document.removeEventListener('pointerup', handlePointerUp)
        document.removeEventListener('pointercancel', handlePointerCancel)
      }

      const handlePointerDown = (e: PointerEvent) => {
        isDraggingRef.current = true
        dragStartXRef.current = e.clientX
        dragStartOffsetRef.current = offsetRef.current
        lastDragXRef.current = e.clientX
        lastDragTimeRef.current = performance.now()
        userVelRef.current = 0

        // Add document-level listeners to track pointer even outside element
        document.addEventListener('pointermove', handlePointerMove)
        document.addEventListener('pointerup', handlePointerUp)
        document.addEventListener('pointercancel', handlePointerCancel)

        ensureRaf()
        e.preventDefault()
      }

      rootEl.addEventListener('pointerdown', handlePointerDown)

      return () => {
        rootEl.removeEventListener('pointerdown', handlePointerDown)
        // Clean up document listeners in case they're still attached
        document.removeEventListener('pointermove', handlePointerMove)
        document.removeEventListener('pointerup', handlePointerUp)
        document.removeEventListener('pointercancel', handlePointerCancel)
      }
    }, [ensureRaf])

    // 🎞️ RAF loop
    useEffect(() => {
      ensureRaf()
      return () => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current)
        }
        rafRef.current = undefined
        lastTimeRef.current = undefined
      }
    }, [ensureRaf])

    // 🖱️ Wheel scroll. The delta is committed to the transform *synchronously*
    // in the event handler so the first paint reflects the user's input even
    // when RAF is delayed by parent re-renders (the cause of the "delayed
    // start" the marquee was showing on the Explore page). A reduced impulse
    // still feeds the velocity model for a short momentum tail.
    const [wheelTarget, setWheelTarget] = useState<HTMLDivElement | undefined>(
      undefined,
    )

    useLayoutEffect(() => {
      const rootEl = wheelTarget
      if (!rootEl) {
        return
      }

      const SENSITIVITY = 1.0
      const MAX_USER_VEL = 5000
      // Reduced from the previous 15: the immediate 1:1 sync commit below now
      // delivers the bulk of the displacement, so the impulse just provides a
      // brief momentum tail.
      const IMPULSE_MULTIPLIER = 3

      const onWheel = (e: WheelEvent) => {
        const delta =
          Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY
        if (delta === 0) {
          return
        }
        e.preventDefault()

        offsetRef.current = offsetRef.current + delta
        commitOffset()

        const impulse = delta * SENSITIVITY * IMPULSE_MULTIPLIER
        userVelRef.current = Math.max(
          -MAX_USER_VEL,
          Math.min(MAX_USER_VEL, userVelRef.current + impulse),
        )
        ensureRaf()
      }

      rootEl.addEventListener('wheel', onWheel, { passive: false })
      return () => {
        rootEl.removeEventListener('wheel', onWheel)
      }
    }, [ensureRaf, commitOffset, wheelTarget])

    const handleRootRef = useCallback(
      (el: HTMLDivElement | null) => {
        rootRef.current = el
        setWheelTarget((prev) => (prev === el ? prev : (el ?? undefined)))
        if (typeof forwardedRef === 'function') {
          forwardedRef(el)
        } else if (forwardedRef) {
          forwardedRef.current = el
        }
      },
      [forwardedRef],
    )

    const wrappedChildren = useMemo(
      () =>
        Children.map(children, (child, index) => (
          <InlineWrapper key={index}>{child}</InlineWrapper>
        )),
      [children],
    )

    const measuredChildren = useMemo(
      () =>
        Children.map(children, (child, index) => (
          <Child key={index}>{child}</Child>
        )),
      [children],
    )

    const multiplyChildren = useCallback(
      (count: number) =>
        Array.from({ length: Math.max(0, count) }, (_, i) => (
          <Fragment key={i}>{wrappedChildren}</Fragment>
        )),
      [wrappedChildren],
    )

    return (
      <MarqueeWheelRootContext.Provider value={wheelTarget}>
        <RootWrapper>
          <Root ref={handleRootRef}>
            <LaneWrapper ref={laneRef}>
              <SingleGroup ref={contentRef}>{measuredChildren}</SingleGroup>
              {multiplyChildren(Math.max(1, multiplier))}
            </LaneWrapper>
          </Root>
          <NavigationButton
            onMouseEnter={() => {
              pausedRef.current = true
            }}
            onMouseLeave={() => {
              pausedRef.current = false
              ensureRaf()
            }}
            $side="left"
            type="button"
            onClick={handlePreviousClick}
          >
            <ChevronLeftExactIcon size={10} />
          </NavigationButton>
          <NavigationButton
            onMouseEnter={() => {
              pausedRef.current = true
            }}
            onMouseLeave={() => {
              pausedRef.current = false
              ensureRaf()
            }}
            $side="right"
            type="button"
            onClick={handleNextClick}
          >
            <ChevronRightExactIcon size={10} />
          </NavigationButton>
        </RootWrapper>
      </MarqueeWheelRootContext.Provider>
    )
  },
)

const Root = styled.div`
  overflow: hidden;
  width: 100%;
  position: relative;
  display: block;
  -webkit-user-select: none;
  user-select: none;
  touch-action: pan-y;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`

const LaneWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  will-change: transform;
  transform: translate3d(0, 0, 0);
`

const SingleGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 0 0 auto;
`

const Child = styled.div`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
`

const InlineWrapper = styled.div`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
`

const RootWrapper = styled.div`
  position: relative;
  overflow: hidden;
`

const NavigationButton = styled.button<{ $side: 'left' | 'right' }>`
  all: unset;
  position: absolute;
  ${(x) =>
    x.$side === 'left'
      ? css`
          left: 0;
        `
      : css`
          right: 0;
        `}
  top: 0;
  bottom: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  background: var(--appearance-1);
  padding: 2px 4px;
  opacity: 0;
  transition: all 0.2s ease;
  color: var(--text-12);

  &:hover {
    opacity: 1 !important;
  }

  ${RootWrapper}:hover & {
    opacity: 0.6;
  }
`
